import React, { useState, useCallback, useEffect } from 'react';
import Settings from './Settings';
import FloatingPhone from './FloatingPhone';
import Notepad from './Notepad';
import Search from './Search';

const Desktop = ({ onOpenGoodVibes }) => {
    const [iconPositions, setIconPositions] = useState({
        goodVibes: { x: 20, y: 20 },
        myComputer: { x: 20, y: 100 },
        recycleBin: { x: 20, y: 180 },
        internetExplorer: { x: 20, y: 260 },
        notepad: { x: 20, y: 340 },
        settings: { x: 20, y: 420 },
        emojiBoard: { x: 20, y: 500 }
    });

    const [draggedIcon, setDraggedIcon] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [showSettings, setShowSettings] = useState(false);
    const [showStartMenu, setShowStartMenu] = useState(false);
    const [showEmojiBoard, setShowEmojiBoard] = useState(false);
    const [showNotepad, setShowNotepad] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [floatingEmojis, setFloatingEmojis] = useState([]);
    const [emojiBoardPosition, setEmojiBoardPosition] = useState({ x: 0, y: 0 });
    const [isDraggingEmojiBoard, setIsDraggingEmojiBoard] = useState(false);
    const [emojiBoardDragOffset, setEmojiBoardDragOffset] = useState({ x: 0, y: 0 });
    const [settings, setSettings] = useState({
        fontSize: 16,
        iconSize: 70,
        background: 'url(/images/backgrounds/bg.png) center/cover no-repeat'
    });
    const [isMobile, setIsMobile] = useState(false);
    const [mobileIconPositions, setMobileIconPositions] = useState({});
    const [openWindows, setOpenWindows] = useState([]);
    const [activeWindow, setActiveWindow] = useState(null);
    const [taskbarBlink, setTaskbarBlink] = useState(false);

    // Mobile detection and responsive positioning
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);

            if (mobile) {
                // Arrange icons in a grid for mobile
                const icons = ['goodVibes', 'myComputer', 'recycleBin', 'internetExplorer', 'notepad', 'settings', 'emojiBoard'];
                const positions = {};
                const cols = 3;
                const iconSize = 80;
                const spacing = 20;

                icons.forEach((icon, index) => {
                    const row = Math.floor(index / cols);
                    const col = index % cols;
                    positions[icon] = {
                        x: spacing + col * (iconSize + spacing),
                        y: spacing + row * (iconSize + spacing)
                    };
                });
                setMobileIconPositions(positions);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleMouseDown = (e, iconName) => {
        e.preventDefault();
        setDraggedIcon(iconName);
        const rect = e.currentTarget.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    const handleMouseMove = useCallback((e) => {
        if (draggedIcon) {
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;

            setIconPositions(prev => ({
                ...prev,
                [draggedIcon]: { x: Math.max(0, newX), y: Math.max(0, newY) }
            }));
        }
    }, [draggedIcon, dragOffset]);

    const handleMouseUp = () => {
        setDraggedIcon(null);
    };

    const handleSettingsChange = (newSettings) => {
        setSettings(newSettings);
    };

    const openSettings = () => {
        setShowSettings(true);
        addWindowToTaskbar('settings', 'Settings', '⚙️');
    };

    const openNotepad = () => {
        setShowNotepad(true);
        addWindowToTaskbar('notepad', 'User Manual', '📝');
    };

    const openSearch = () => {
        setShowSearch(true);
        addWindowToTaskbar('search', 'Good Vibes Search', '🌐');
    };

    // Window management functions
    const addWindowToTaskbar = (id, title, icon) => {
        setOpenWindows(prev => {
            const exists = prev.find(w => w.id === id);
            if (!exists) {
                return [...prev, { id, title, icon, active: true }];
            }
            return prev.map(w => w.id === id ? { ...w, active: true } : w);
        });
        setActiveWindow(id);
    };

    const removeWindowFromTaskbar = (id) => {
        setOpenWindows(prev => prev.filter(w => w.id !== id));
        setActiveWindow(null);
    };

    const focusWindow = (id) => {
        setActiveWindow(id);
        setOpenWindows(prev => prev.map(w => ({ ...w, active: w.id === id })));
    };

    const startTaskbarBlink = () => {
        setTaskbarBlink(true);
        setTimeout(() => setTaskbarBlink(false), 3000);
    };

    // Expose the notification function globally
    useEffect(() => {
        window.desktopRef = {
            startTaskbarBlink
        };
        return () => {
            window.desktopRef = null;
        };
    }, []);

    const toggleStartMenu = (e) => {
        e.stopPropagation();
        setShowStartMenu(!showStartMenu);
    };

    const closeStartMenu = () => {
        setShowStartMenu(false);
    };

    const toggleEmojiBoard = (e) => {
        e.stopPropagation();
        setShowEmojiBoard(!showEmojiBoard);
    };

    const addFloatingEmoji = (emoji) => {
        const newEmoji = {
            id: Date.now() + Math.random(),
            emoji: emoji,
            x: Math.random() * (window.innerWidth - 100),
            y: Math.random() * (window.innerHeight - 100),
            rotation: Math.random() * 360,
            scale: 0.5 + Math.random() * 0.5,
            animationDuration: 3 + Math.random() * 4
        };

        setFloatingEmojis(prev => [...prev, newEmoji]);

        // Remove emoji after animation
        setTimeout(() => {
            setFloatingEmojis(prev => prev.filter(e => e.id !== newEmoji.id));
        }, newEmoji.animationDuration * 1000);
    };

    const cleanupEmojis = () => {
        setFloatingEmojis([]);
    };

    React.useEffect(() => {
        if (draggedIcon) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [draggedIcon, dragOffset, handleMouseMove]);

    return (
        <div
            className="desktop"
            style={{
                background: settings.background,
                backgroundImage: 'none',
                fontSize: `${settings.fontSize}px`
            }}
            onClick={(e) => {
                // Only close start menu if clicking on desktop itself, not on icons
                if (e.target === e.currentTarget) {
                    closeStartMenu();
                }
            }}
        >
            {/* Floating Nokia Phone */}
            <FloatingPhone />

            {/* Desktop Icons */}
            <div className="desktop-icons">
                {/* Good Vibes App Icon */}
                <div
                    className={`desktop-icon ${draggedIcon === 'goodVibes' ? 'dragging' : ''} ${isMobile ? 'mobile-icon' : ''}`}
                    style={{
                        position: 'absolute',
                        left: (isMobile ? mobileIconPositions.goodVibes : iconPositions.goodVibes).x,
                        top: (isMobile ? mobileIconPositions.goodVibes : iconPositions.goodVibes).y
                    }}
                    onMouseDown={(e) => handleMouseDown(e, 'goodVibes')}
                    onDoubleClick={onOpenGoodVibes}
                >
                    <div className="icon-image" style={{ width: `${settings.iconSize}px`, height: `${settings.iconSize}px` }}>
                        <div className="emoji-nail-polish">💅🏻</div>
                    </div>
                    <div className="icon-label">GOOD VIBES* #ccfc</div>
                </div>

                {/* Other classic Windows 98 icons */}
                <div
                    className={`desktop-icon ${draggedIcon === 'myComputer' ? 'dragging' : ''} ${isMobile ? 'mobile-icon' : ''}`}
                    style={{
                        position: 'absolute',
                        left: (isMobile ? mobileIconPositions.myComputer : iconPositions.myComputer).x,
                        top: (isMobile ? mobileIconPositions.myComputer : iconPositions.myComputer).y
                    }}
                    onMouseDown={(e) => handleMouseDown(e, 'myComputer')}
                >
                    <div className="icon-image" style={{ width: `${settings.iconSize}px`, height: `${settings.iconSize}px` }}>
                        <div className="my-computer">💻</div>
                    </div>
                    <div className="icon-label">My Computer</div>
                </div>

                <div
                    className={`desktop-icon ${draggedIcon === 'recycleBin' ? 'dragging' : ''} ${isMobile ? 'mobile-icon' : ''}`}
                    style={{
                        position: 'absolute',
                        left: (isMobile ? mobileIconPositions.recycleBin : iconPositions.recycleBin).x,
                        top: (isMobile ? mobileIconPositions.recycleBin : iconPositions.recycleBin).y
                    }}
                    onMouseDown={(e) => handleMouseDown(e, 'recycleBin')}
                    onDoubleClick={cleanupEmojis}
                >
                    <div className="icon-image" style={{ width: `${settings.iconSize}px`, height: `${settings.iconSize}px` }}>
                        <div className="recycle-bin">🗑️</div>
                    </div>
                    <div className="icon-label">Trash Bin</div>
                </div>

                <div
                    className={`desktop-icon ${draggedIcon === 'internetExplorer' ? 'dragging' : ''} ${isMobile ? 'mobile-icon' : ''}`}
                    style={{
                        position: 'absolute',
                        left: (isMobile ? mobileIconPositions.internetExplorer : iconPositions.internetExplorer).x,
                        top: (isMobile ? mobileIconPositions.internetExplorer : iconPositions.internetExplorer).y
                    }}
                    onMouseDown={(e) => handleMouseDown(e, 'internetExplorer')}
                    onDoubleClick={openSearch}
                >
                    <div className="icon-image" style={{ width: `${settings.iconSize}px`, height: `${settings.iconSize}px` }}>
                        <div className="internet-explorer">🌐</div>
                    </div>
                    <div className="icon-label">Internet Explorer</div>
                </div>

                <div
                    className={`desktop-icon ${draggedIcon === 'notepad' ? 'dragging' : ''} ${isMobile ? 'mobile-icon' : ''}`}
                    style={{
                        position: 'absolute',
                        left: (isMobile ? mobileIconPositions.notepad : iconPositions.notepad).x,
                        top: (isMobile ? mobileIconPositions.notepad : iconPositions.notepad).y
                    }}
                    onMouseDown={(e) => handleMouseDown(e, 'notepad')}
                    onDoubleClick={openNotepad}
                >
                    <div className="icon-image" style={{ width: `${settings.iconSize}px`, height: `${settings.iconSize}px` }}>
                        <div className="notepad">📝</div>
                    </div>
                    <div className="icon-label">Notepad</div>
                </div>

                {/* Settings Icon */}
                <div
                    className={`desktop-icon ${draggedIcon === 'settings' ? 'dragging' : ''} ${isMobile ? 'mobile-icon' : ''}`}
                    style={{
                        position: 'absolute',
                        left: (isMobile ? mobileIconPositions.settings : iconPositions.settings).x,
                        top: (isMobile ? mobileIconPositions.settings : iconPositions.settings).y
                    }}
                    onMouseDown={(e) => handleMouseDown(e, 'settings')}
                    onDoubleClick={openSettings}
                >
                    <div className="icon-image" style={{ width: `${settings.iconSize}px`, height: `${settings.iconSize}px` }}>
                        <div className="settings-icon">⚙️</div>
                    </div>
                    <div className="icon-label">Settings</div>
                </div>

                {/* Emoji Board Icon */}
                <div
                    className={`desktop-icon ${draggedIcon === 'emojiBoard' ? 'dragging' : ''} ${isMobile ? 'mobile-icon' : ''}`}
                    style={{
                        position: 'absolute',
                        left: (isMobile ? mobileIconPositions.emojiBoard : iconPositions.emojiBoard).x,
                        top: (isMobile ? mobileIconPositions.emojiBoard : iconPositions.emojiBoard).y,
                        zIndex: 1000
                    }}
                    onMouseDown={(e) => handleMouseDown(e, 'emojiBoard')}
                    onDoubleClick={toggleEmojiBoard}
                >
                    <div className="icon-image" style={{ width: `${settings.iconSize}px`, height: `${settings.iconSize}px` }}>
                        <div className="emoji-board-icon">😀</div>
                    </div>
                    <div className="icon-label">Emojis</div>
                </div>
            </div>

            {/* Taskbar */}
            <div className={`taskbar ${isMobile ? 'mobile-taskbar' : ''} ${taskbarBlink ? 'taskbar-blink' : ''}`}>
                <div
                    className="start-button"
                    onClick={toggleStartMenu}
                    style={{ zIndex: 3000 }}
                >
                    <span>Start</span>
                </div>

                {/* Open Windows */}
                <div className="taskbar-windows">
                    {openWindows.map(window => (
                        <div
                            key={window.id}
                            className={`taskbar-window ${window.active ? 'active' : ''}`}
                            onClick={() => focusWindow(window.id)}
                        >
                            <span className="window-icon">{window.icon}</span>
                            <span className="window-title">{window.title}</span>
                        </div>
                    ))}
                </div>

                <div className="taskbar-time">
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
            </div>

            {/* Start Menu */}
            {showStartMenu && (
                <div className="start-menu" onClick={(e) => e.stopPropagation()}>
                    <div className="start-menu-left">
                        <div className="windows-logo">ccfcOS</div>
                        <div className="start-button-mini">Start</div>
                    </div>
                    <div className="start-menu-content">
                        <div className="menu-section">
                            <div className="menu-item">
                                <div className="menu-icon">📁</div>
                                <span>Programs</span>
                                <div className="menu-arrow">▶</div>
                            </div>
                            <div className="menu-item">
                                <div className="menu-icon">📄</div>
                                <span>Documents</span>
                                <div className="menu-arrow">▶</div>
                            </div>
                            <div className="menu-item">
                                <div className="menu-icon">⚙️</div>
                                <span>Settings</span>
                                <div className="menu-arrow">▶</div>
                            </div>
                            <div className="menu-item">
                                <div className="menu-icon">🔍</div>
                                <span>Find</span>
                                <div className="menu-arrow">▶</div>
                            </div>
                            <div className="menu-item">
                                <div className="menu-icon">❓</div>
                                <span>Help</span>
                            </div>
                            <div className="menu-item">
                                <div className="menu-icon">▶</div>
                                <span>Run...</span>
                            </div>
                        </div>
                        <div className="menu-separator"></div>
                        <div className="menu-section">
                            <div className="menu-item">
                                <div className="menu-icon">⏸️</div>
                                <span>Suspend</span>
                            </div>
                            <div className="menu-item" onClick={() => window.location.reload()}>
                                <div className="menu-icon">🔄</div>
                                <span>Restart</span>
                            </div>
                            <div className="menu-item">
                                <div className="menu-icon">⏹️</div>
                                <span>Shut Down...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Emoji Board */}
            {showEmojiBoard && (
                <div className={`emoji-board active ${isMobile ? 'mobile-fullscreen-window' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div className="emoji-board-header">
                        <h3>Choose an Emoji</h3>
                        <button
                            className="close-board-btn"
                            onClick={() => setShowEmojiBoard(false)}
                        >
                            ×
                        </button>
                    </div>
                    <div className="emoji-instruction">
                        Double click trash bin to clean up
                    </div>
                    <div className="emoji-grid">
                        {[
                            '😀', '😊', '😍', '🥰', '😘', '🤗', '😎', '🤩', '🥳', '🎉',
                            '🎊', '🎈', '🎁', '💖', '💕', '💗', '💝', '💎', '✨', '🌟',
                            '⭐', '🌈', '🦄', '🦋', '🌸', '🌺', '🌻', '🌷', '🌹', '🌼',
                            '🍀', '🌿', '🌱', '🌵', '🌴', '🌳', '🌲', '🌰', '🎨', '🎭',
                            '🎪', '🎡', '🎢', '🎠', '🎯', '🎲', '🎮', '🕹️', '🍕', '🍔',
                            '🍟', '🍰', '🧁', '🍭', '🍫', '🍬', '🍪', '🥤', '☕', '🍵',
                            '🍺', '🍷', '🥂', '🍾', '🍻', '🥃', '🍸', '🍹', '🍾', '🥤',
                            '🚗', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🛻',
                            '🚚', '🚛', '🚜', '🏍️', '🛵', '🚲', '🛴', '🛹', '🛼', '🚁',
                            '✈️', '🛩️', '🛫', '🛬', '🪂', '🚀', '🛸', '🚉', '🚊', '🚝',
                            '🚞', '🚋', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂',
                            '🚆', '🚇', '🚊', '🚉', '✈️', '🛫', '🛬', '🛩️', '🛸', '🚀'
                        ].map((emoji, index) => (
                            <button
                                key={index}
                                className="emoji-option"
                                onClick={() => addFloatingEmoji(emoji)}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Floating Emojis */}
            {floatingEmojis.map((emoji) => (
                <div
                    key={emoji.id}
                    className="floating-emoji"
                    style={{
                        position: 'fixed',
                        left: emoji.x,
                        top: emoji.y,
                        transform: `rotate(${emoji.rotation}deg) scale(${emoji.scale})`,
                        animation: `floatEmoji ${emoji.animationDuration}s ease-in-out forwards`,
                        zIndex: 1000,
                        pointerEvents: 'none',
                        fontSize: '32px'
                    }}
                >
                    {emoji.emoji}
                </div>
            ))}

            {/* Credits */}
            <div className="credits">
                <div className="credits-content">
                    <div className="credit-line">1. made by #ccfc</div>
                    <div className="credit-line">2. cc-fc.cc</div>
                    <div className="credit-line">3. San Francisco, CA</div>
                    <div className="credit-line">4. made in 2025</div>
                </div>
            </div>

            {/* Settings Window */}
            {showSettings && (
                <Settings
                    onClose={() => {
                        setShowSettings(false);
                        removeWindowFromTaskbar('settings');
                    }}
                    onSettingsChange={handleSettingsChange}
                    currentSettings={settings}
                    isMobile={isMobile}
                />
            )}

            {/* Notepad Window */}
            {showNotepad && (
                <Notepad
                    onClose={() => {
                        setShowNotepad(false);
                        removeWindowFromTaskbar('notepad');
                    }}
                    isMobile={isMobile}
                />
            )}

            {/* Search Window */}
            {showSearch && (
                <Search
                    onClose={() => {
                        setShowSearch(false);
                        removeWindowFromTaskbar('search');
                    }}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

export default Desktop;
